import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Grid,
  Avatar,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Badge
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorIcon from '@mui/icons-material/Error';
import moment from 'moment';

// 訪客通行元件
const VisitorAccess = () => {
  const [status, setStatus] = useState('valid'); // valid, expired, used
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0 });
  const [accessUsed, setAccessUsed] = useState(false);
  const [scanning, setScanning] = useState(false);
  
  const { visitor } = useSelector(state => state.auth);
  
  // 計算剩餘時間
  useEffect(() => {
    if (!visitor) return;
    
    const updateTimeRemaining = () => {
      const now = moment();
      const expiry = moment(visitor.accessTo);
      
      if (now.isAfter(expiry)) {
        setStatus('expired');
        setTimeRemaining({ hours: 0, minutes: 0 });
        return;
      }
      
      const duration = moment.duration(expiry.diff(now));
      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.asMinutes()) % 60;
      
      setTimeRemaining({ hours, minutes });
    };
    
    updateTimeRemaining();
    const timer = setInterval(updateTimeRemaining, 60000); // 每分鐘更新
    
    return () => clearInterval(timer);
  }, [visitor]);
  
  // 模擬使用通行證
  const handleUseAccess = () => {
    setScanning(true);
    
    // 模擬掃描過程
    setTimeout(() => {
      setScanning(false);
      setAccessUsed(true);
      setStatus('used');
    }, 2000);
  };
  
  if (!visitor) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>載入訪客資訊中...</Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>訪客通行</Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Chip 
              label={
                status === 'valid' ? '通行證有效' :
                status === 'expired' ? '通行證已過期' :
                '通行證已使用'
              }
              color={
                status === 'valid' ? 'success' :
                status === 'expired' ? 'error' :
                'default'
              }
              sx={{ py: 2, px: 3, fontSize: '1rem' }}
            />
          </Box>
          
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar 
              sx={{ 
                width: 100, 
                height: 100, 
                bgcolor: status === 'valid' ? 'success.main' : 'grey.300',
                mx: 'auto',
                mb: 2
              }}
            >
              <VpnKeyIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h3" gutterBottom>
              {visitor.otpCode}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              一次性通行密碼
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  訪問目的地
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    {visitor.building} {visitor.unit}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  有效時間
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    {moment(visitor.accessFrom).format('MM/DD HH:mm')} - {moment(visitor.accessTo).format('MM/DD HH:mm')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  訪客資訊
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    {visitor.name}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, mb: 2, textAlign: 'center' }}>
            {status === 'valid' && (
              <>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  通行證有效時間剩餘
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {timeRemaining.hours}小時 {timeRemaining.minutes}分鐘
                </Typography>
              </>
            )}
            
            {status === 'expired' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                此通行證已經過期，請聯絡單位住戶獲取新的通行證
              </Alert>
            )}
            
            {status === 'used' && (
              <Alert severity="success" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ mr: 1 }} />
                通行證已成功使用，歡迎訪問
              </Alert>
            )}
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<QrCodeScannerIcon />}
              disabled={status !== 'valid' || scanning}
              onClick={handleUseAccess}
              sx={{ px: 4, py: 1.5 }}
            >
              {scanning ? <CircularProgress size={24} color="inherit" /> : '掃描進入'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>訪客須知</Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <Badge color="primary" variant="dot">
                <Avatar sx={{ bgcolor: 'primary.light' }}>1</Avatar>
              </Badge>
            </ListItemIcon>
            <ListItemText 
              primary="通行證僅在指定時間內有效" 
              secondary="超出有效時間將無法使用"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <Badge color="primary" variant="dot">
                <Avatar sx={{ bgcolor: 'primary.light' }}>2</Avatar>
              </Badge>
            </ListItemIcon>
            <ListItemText 
              primary="通行證僅能使用一次" 
              secondary="使用後自動失效"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <Badge color="primary" variant="dot">
                <Avatar sx={{ bgcolor: 'primary.light' }}>3</Avatar>
              </Badge>
            </ListItemIcon>
            <ListItemText 
              primary="請遵守大廈管理規則" 
              secondary="如有問題請聯絡保安或管理處"
            />
          </ListItem>
        </List>
      </Box>
      
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <MeetingRoomIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              訪問指引
            </Typography>
          </Box>
          
          <Typography variant="body2" paragraph>
            請在抵達大廈時，向大堂保安出示此通行證或掃描大廈入口處的通行碼掃描器。
          </Typography>
          
          <Typography variant="body2" paragraph>
            進入後，請按照大廈指示前往目的地。如有疑問，可向大堂保安尋求協助。
          </Typography>
          
          <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
            前往 {visitor.building} {visitor.unit}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

// 主儀表板組件，處理路由
const VisitorDashboard = () => {
  return (
    <Routes>
      <Route path="/" element={<VisitorAccess />} />
      <Route path="/access" element={<VisitorAccess />} />
    </Routes>
  );
};

export default VisitorDashboard; 